import { graphqlFetch } from '@/utils/graphqlFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CREATE_SECTION,
  REMOVE_SECTION,
  UPDATE_SECTION,
} from '../mutations/module';
import {
  SectionInput,
  SectionOutput,
  SectionUpdateInput,
} from '../gql/graphql';
import { GET_SECTION, GET_SECTIONS } from '../queries/module';

export const useCreateSection = () => {
  const queryClient = useQueryClient();
  return useMutation<SectionOutput, Error, SectionInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        { createSection: SectionOutput },
        { input: SectionInput }
      >(CREATE_SECTION, { input });
      return data.createSection;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['sections'],
      }),
  });
};

export const useGetSections = (lessonId: string) => {
  return useQuery<SectionOutput[], Error>({
    queryKey: ['sections', lessonId],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getSections: SectionOutput[] },
        { lessonId: string }
      >(GET_SECTIONS, { lessonId });
      return data.getSections;
    },
    enabled: !!lessonId,
  });
};

export const useGetSection = (id: string) => {
  return useQuery<SectionOutput, Error>({
    queryKey: ['section', id],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getSection: SectionOutput },
        { id: string }
      >(GET_SECTION, { id });
      return data.getSection;
    },
    enabled: !!id,
  });
};

export const useUpdateSection = () => {
  const queryClient = useQueryClient();
  return useMutation<
    SectionOutput,
    Error,
    { id: string; input: SectionUpdateInput }
  >({
    mutationFn: async ({ id, input }) => {
      const data = await graphqlFetch<
        { updateSection: SectionOutput },
        { id: string; input: SectionUpdateInput }
      >(UPDATE_SECTION, { id, input });

      return data.updateSection;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['section', variables.id] });
    },
  });
};

export const useRemoveQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      await graphqlFetch(REMOVE_SECTION, { id });

      return true;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['sections'],
      }),
  });
};
